/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isCollection from '@stdlib/assert/is-collection';
import { isPrimitive as isNumber } from '@stdlib/assert/is-number';
import dtypes from '@stdlib/array/dtypes';
import dtype from '@stdlib/array/dtype';
import contains from '@stdlib/array/base/assert/contains';
import join from '@stdlib/array/base/join';
import { ndarray as strided } from './../../../strided/nanvariance';
import format from '@stdlib/string/format';


// VARIABLES //

var IDTYPES = dtypes( 'real_and_generic' );
var GENERIC_DTYPE = 'generic';


// MAIN //

/**
* Computes the variance of an array ignoring `NaN` values.
*
* @param {NumericArray} x - input array
* @param {number} [correction=1.0] - degrees of freedom adjustment
* @throws {TypeError} first argument must be an array-like object
* @throws {TypeError} first argument must have a supported data type
* @throws {TypeError} second argument must be a number
* @returns {number} variance
*
* @example
* var x = [ 1.0, -2.0, NaN, 2.0 ];
*
* var v = nanvariance( x, 1.0 );
* // returns ~4.3333
*/
function nanvariance( x ) {
	var correction;
	var dt;
	if ( !isCollection( x ) ) {
		throw new TypeError( format( 'invalid argument. First argument must be an array-like object. Value: `%s`.', x ) );
	}
	dt = dtype( x ) || GENERIC_DTYPE;
	if ( !contains( IDTYPES, dt ) ) {
		throw new TypeError( format( 'invalid argument. First argument must have one of the following data types: "%s". Data type: `%s`.', join( IDTYPES, '", "' ), dt ) );
	}
	if ( arguments.length > 1 ) {
		correction = arguments[ 1 ];
		if ( !isNumber( correction ) ) {
			throw new TypeError( format( 'invalid argument. Second argument must be a number. Value: `%s`.', correction ) );
		}
	} else {
		correction = 1.0;
	}
	return strided( x.length, correction, x, 1, 0 );
}


// EXPORTS //

export default nanvariance;
